/**
 * AdminContestTabs — shared sub-navigation across the three
 * `/admin/contest/*` admin surfaces (config, entries, ranking).
 *
 * Why: Phase C shipped three separate admin pages
 * (`AdminContestConfigPage`, `AdminContestEntriesPage`,
 * `AdminContestRankingPage`) but never wired cross-page navigation
 * between them, and the AdminShell sidebar had no top-level
 * "Contest" entry. Result: even an admin who bookmarks one contest
 * subroute cannot jump to the other two without knowing the URL.
 * This tab-strip is the missing link.
 *
 * Mounted as the FIRST child of each contest admin page's root
 * `<div className="space-y-6">` container so the strip stays
 * above the page-specific heading and the existing `space-y-6`
 * rhythm cascades naturally.
 *
 * Family L discipline — no inline hex, no `prefers-color-scheme`.
 * Active tab uses the `--pp-orange` token via Tailwind's
 * `border-pp-orange` and `text-pp-text` (both theme-flipped).
 */
import React from "react";
import { NavLink } from "react-router-dom";
import { cn } from "@/lib/utils";

const TABS = [
  {
    to: "/admin/contest/config",
    label: "Config",
    testid: "admin-contest-tab-config",
  },
  {
    to: "/admin/contest/entries",
    label: "Moderation queue",
    testid: "admin-contest-tab-entries",
  },
  {
    to: "/admin/contest/ranking",
    label: "Ranking",
    testid: "admin-contest-tab-ranking",
  },
  {
    to: "/admin/contest/archive",
    label: "Archive",
    testid: "admin-contest-tab-archive",
  },
  {
    to: "/admin/contest/slots",
    label: "Slots",
    testid: "admin-contest-tab-slots",
  },
];

export default function AdminContestTabs() {
  return (
    <nav
      aria-label="Contest admin sections"
      data-testid="admin-contest-tabs"
      className="flex flex-wrap items-center gap-1 border-b border-pp-border"
    >
      {TABS.map((t) => (
        <NavLink
          key={t.to}
          to={t.to}
          end
          data-testid={t.testid}
          className={({ isActive }) =>
            cn(
              "px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors",
              isActive
                ? "border-pp-orange text-pp-text"
                : "border-transparent text-pp-text-soft hover:text-pp-text",
            )
          }
        >
          {t.label}
        </NavLink>
      ))}
    </nav>
  );
}

// Exported for the drift-guard test — asserting the tab count +
// destinations stay in sync with the App.js `/admin/contest/*`
// route table.
export const _ADMIN_CONTEST_TABS = TABS;
